import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';

const ProtectionDonneesPage = () => {
  return (
    <>
      <SEO
        title="Politique de protection des données — CarMaintenance (nLPD / RGPD)"
        description="Découvrez comment CarMaintenance collecte, utilise et protège vos données personnelles, conformément à la nouvelle Loi fédérale suisse (nLPD) et au RGPD."
        path="/protection-donnees"
      />
      <PageHero
        title="Politique de protection des données"
        subtitle={
          <>
            Conforme à la nouvelle Loi fédérale sur la protection des données
            (nLPD, en vigueur depuis le 1<sup>er</sup> septembre 2023) et au
            RGPD pour les visiteurs résidant dans l'UE.
          </>
        }
      />
      <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          <Card>
            <CardContent className="p-6 space-y-6">
            <p className="text-gray-700">
              Nous accordons une importance capitale à la protection de vos données personnelles. La présente politique a été établie afin de vous informer de manière transparente sur la manière dont nous collectons, utilisons, partageons et protégeons vos informations. En utilisant le site internet CarMaintenance, vous consentez aux pratiques décrites ci-après.
            </p>

            {/* === Responsable du traitement === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Responsable du traitement</h3>
              <p className="text-gray-700">
                Le responsable du traitement des données collectées via ce site est :
              </p>
              <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                <p className="font-semibold text-gray-900">Car Maintenance Sàrl</p>
                <p>Square des Nénuphars 4</p>
                <p>1219 Châtelaine, Suisse</p>
                <p className="mt-1">
                  Contact :{' '}
                  <a href="mailto:info@car-main.ch" className="text-[#52a675] hover:underline">
                    info@car-main.ch
                  </a>
                </p>
              </div>
            </section>

            {/* === Collecte et utilisation === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Collecte et utilisation des données personnelles</h3>
              <p className="text-gray-700">
                Nous collectons vos données personnelles de manière licite et transparente, et uniquement pour les finalités décrites. Les données que nous pouvons collecter comprennent, sans s'y limiter : nom, adresse e-mail, adresse postale, numéro de téléphone, informations relatives à votre véhicule (marque, modèle, immatriculation, n° de châssis, carte grise), informations de connexion et données de navigation. Nous utilisons ces données pour répondre à vos demandes, vous fournir nos services d'abonnement et de prise de rendez-vous, gérer la facturation et améliorer notre site.
              </p>
            </section>

            {/* === Base légale === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Base légale du traitement</h3>
              <p className="text-gray-700">
                Nous ne traitons vos données que sur la base d'un fondement légal approprié : votre consentement explicite, l'exécution d'un contrat auquel vous êtes partie (ex. abonnement, rendez-vous), le respect d'une obligation légale ou la protection de nos intérêts légitimes.
              </p>
            </section>

            {/* === Partage === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Partage des données personnelles</h3>
              <p className="text-gray-700">
                Nous ne vendons ni ne louons vos données à des tiers à des fins de marketing direct. Dans certains cas, nous partageons vos données avec des prestataires de services (hébergeur, prestataire de paiement, prestataire d'envoi d'e-mails et SMS, garages partenaires nécessaires à l'exécution de votre abonnement) strictement pour les finalités du contrat.
              </p>
            </section>

            {/* === Durée de conservation === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Durée de conservation</h3>
              <p className="text-gray-700">
                Vos données sont conservées le temps nécessaire à la réalisation des finalités décrites ci-dessus et aux délais légaux applicables (notamment les obligations comptables et fiscales suisses : <strong>conservation jusqu'à 10 ans</strong> à compter de la clôture de l'exercice concerné). Au-delà, les données sont supprimées ou anonymisées.
              </p>
            </section>

            {/* === Transferts à l'étranger === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Transferts à l'étranger</h3>
              <p className="text-gray-700">
                Vos données sont principalement traitées et hébergées en Suisse. Si un transfert hors Suisse est nécessaire, il est uniquement effectué vers des pays disposant d'un niveau de protection adéquat reconnu, ou est encadré par des garanties contractuelles appropriées (clauses contractuelles types).
              </p>
              <p className="text-gray-700 mt-3">
                <strong>Sous-traitants principaux :</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 mt-2 mb-3 space-y-2 marker:text-[#52a675]">
                <li><strong>Infomaniak (Suisse)</strong> — hébergement web et envoi de courriels.</li>
                <li><strong>Stripe</strong> — traitement sécurisé des paiements par carte (données hébergées sur des serveurs avec garanties contractuelles appropriées).</li>
                <li><strong>Prestataires de notifications</strong> — envoi des e-mails et SMS liés au suivi de votre abonnement et de vos rendez-vous.</li>
              </ul>
            </section>

            {/* === Cookies === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Cookies et technologies similaires</h3>
              <p className="text-gray-700 mb-3">
                Notre site utilise différents types de cookies. Nous distinguons :
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="font-semibold text-gray-900">Cookies strictement nécessaires</p>
                  <p className="text-sm mt-1">
                    Ces cookies ne collectent aucune donnée personnelle. Ils permettent uniquement le bon fonctionnement du site (authentification, panier de souscription, sécurité). <strong>Aucun consentement n'est requis</strong> pour ces cookies.
                  </p>
                </li>
                <li className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="font-semibold text-gray-900">Cookies de mesure d'audience et préférences</p>
                  <p className="text-sm mt-1">
                    Ces cookies, lorsqu'ils sont utilisés, nous aident à comprendre l'utilisation du site et à améliorer votre expérience. Ils ne sont déposés qu'avec votre consentement préalable, que vous pouvez retirer à tout moment.
                  </p>
                </li>
              </ul>
            </section>

            {/* === Sécurité === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Sécurité des données</h3>
              <p className="text-gray-700">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre la perte, l'utilisation abusive et l'accès non autorisé (chiffrement HTTPS, contrôles d'accès, sauvegardes régulières). Aucune méthode de transmission ou de stockage n'étant infaillible, nous ne pouvons toutefois garantir une sécurité absolue.
              </p>
            </section>

            {/* === Vos droits === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Vos droits</h3>
              <p className="text-gray-700 mb-2">
                Conformément à la nLPD, vous disposez à tout moment des droits suivants sur vos données :
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>droit d'accès à vos données ;</li>
                <li>droit de rectification des données inexactes ;</li>
                <li>droit de suppression (sous réserve d'obligations légales) ;</li>
                <li>droit de limitation du traitement ;</li>
                <li>droit d'opposition, notamment au marketing direct ;</li>
                <li>droit à la portabilité de vos données.</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Pour exercer ces droits, contactez-nous à{' '}
                <a href="mailto:info@car-main.ch" className="text-[#52a675] hover:underline font-medium">
                  info@car-main.ch
                </a>
                . Vous disposez également du droit d'introduire une plainte auprès du{' '}
                <strong>Préposé fédéral à la protection des données et à la transparence (PFPDT)</strong> :{' '}
                <a
                  href="https://www.edoeb.admin.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#52a675] hover:underline"
                >
                  www.edoeb.admin.ch
                </a>
                .
              </p>
            </section>

            {/* === RGPD === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Visiteurs résidant dans l'Union européenne (RGPD)</h3>
              <p className="text-gray-700">
                Si vous résidez dans un État membre de l'Union européenne, le Règlement général sur la protection des données (RGPD) s'applique également au traitement de vos données. Vous bénéficiez des mêmes droits que ceux décrits ci-dessus et disposez en outre du droit d'introduire une réclamation auprès de l'autorité de contrôle de votre pays de résidence.
              </p>
            </section>

            {/* === Contact === */}
            <section>
              <h3 className="text-xl font-semibold text-[#52a675] mb-3">Contact</h3>
              <p className="text-gray-700">
                Pour toute question relative à la présente politique ou à l'exercice de vos droits, contactez-nous à :{' '}
                <a href="mailto:info@car-main.ch" className="text-[#52a675] hover:underline font-medium">
                  info@car-main.ch
                </a>
                .
              </p>
            </section>

            {/* === Modifications & date === */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-gray-700">
                Nous nous réservons le droit de modifier la présente politique conformément à l'évolution de la législation et de nos pratiques. Toute modification sera publiée sur cette page et entrera en vigueur dès sa publication.
              </p>
              <p className="text-gray-500 text-sm mt-4">
                Dernière mise à jour :{' '}
                {new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </p>
            </div>

            {/* Cross-link */}
            <div className="pt-2 text-sm text-gray-600">
              Voir aussi nos{' '}
              <Link to="/mentions-legales" className="text-[#52a675] hover:underline">
                mentions légales
              </Link>{' '}
              et nos{' '}
              <Link to="/cgv" className="text-[#52a675] hover:underline">
                conditions générales de vente
              </Link>
              .
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  );
};

export default ProtectionDonneesPage;
